import { ICellRendererParams } from '@ag-grid-community/core'
import { FileOutlined } from '@ant-design/icons'
import { Tag, Typography } from 'antd'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import React from 'react'
import { CensusFileWithRowStatsDto } from '../api/dto/census-file-with-row-stats.dto'
import { CensusZendeskTicketJobStatus } from '../api/dto/census-zendesk-ticket-job.dto'
import ReviewActionsCell from '../components/ReviewActionsCell'

const ZENDESK_STATUS_DISPLAY: Record<
  CensusZendeskTicketJobStatus,
  { color: string; label: string }
> = {
  pending: { color: 'default', label: 'Pending' },
  processing: { color: 'processing', label: 'Processing' },
  completed: { color: 'success', label: 'Completed' },
  completed_with_failures: {
    color: 'warning',
    label: 'Completed with failures',
  },
  failed: { color: 'error', label: 'Failed' },
}

const { Text, Link } = Typography

const submittedColumn = {
  headerName: 'Submitted',
  field: 'rowLatestUpdatedAt',
  flex: 1,
  cellRenderer: (params) => (
    <Text className="textSm">{relativeTimeString(params.value)}</Text>
  ),
}

// Gets AGGrid column definitions for pages that show rows of files
export const getCensusFilesColDefs = ({
  clientNameLookup,
  hasSubmittedColumn = false,
  hasZendeskStatusColumn = false,
  clickView,
  isSurecoAdmin = false,
}: {
  clientNameLookup: any
  hasSubmittedColumn: boolean
  hasZendeskStatusColumn?: boolean
  clickView: (publicKey: string) => void
  isSurecoAdmin?: boolean
}) => {
  const colDefs = [
    {
      headerName: 'Uploaded',
      field: 'createdAt',
      flex: 1,
      cellRenderer: (params) => (
        <Text className="textSm">{relativeTimeString(params.value, 1)}</Text>
      ),
    },
    {
      headerName: 'Customer',
      field: 'clientPublicKey',
      flex: 1,
      sortable: false,
      cellRenderer: (params) => (
        <Text className="textEmphasis">
          {clientNameLookup[params.value] ?? 'loading...'}
        </Text>
      ),
    },
    {
      headerName: 'Records',
      field: 'rowTypeCount',
      flex: 1,
      cellRenderer: (params) => (
        <Text className="textEmphasis textGray">{`${params.value} of ${params.data.rowTotalCount}`}</Text>
      ),
    },
    {
      headerName: 'Uploaded by',
      field: 'uploadByEmail',
      flex: 1,
      sortable: false,
      cellRenderer: (
        params: ICellRendererParams<CensusFileWithRowStatsDto>
      ) => <Text className="textSm">{params?.value ?? '-'}</Text>,
    },
    {
      headerName: 'File',
      field: 'fileName',
      flex: 1,
      cellRenderer: (params) => {
        return (
          <>
            <FileOutlined style={{ fontSize: '14px', marginRight: '8px' }} />
            {params.data.presignedDownloadUrl ? (
              <Link
                href={params.data.presignedDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#b0b0b0' }}
                className="textSm textGray"
              >
                {params.value}
              </Link>
            ) : (
              <Text className="textSm textGray">{params.value}</Text>
            )}
          </>
        )
      },
    },
    {
      headerName: '',
      field: 'review',
      cellRenderer: (params) => (
        <ReviewActionsCell
          params={params}
          clickView={clickView}
          isSurecoAdmin={isSurecoAdmin}
        />
      ),
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      width: isSurecoAdmin ? 170 : 120,
    },
  ]
  if (hasSubmittedColumn) {
    colDefs.splice(1, 0, submittedColumn)
  }
  if (isSurecoAdmin) {
    const hiddenFromHRColumn = {
      headerName: 'Hidden from HR',
      field: 'hiddenFromHR',
      flex: 1,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<CensusFileWithRowStatsDto>) =>
        params.value ? (
          <Tag color="warning">Hidden</Tag>
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        ),
    }
    // Insert before the File column (second-to-last, before the Review button)
    colDefs.splice(colDefs.length - 2, 0, hiddenFromHRColumn)
  }
  if (hasZendeskStatusColumn) {
    const zendeskStatusColumn = {
      headerName: 'Zendesk Status',
      field: 'latestZendeskJobStatus',
      flex: 1,
      sortable: false,
      cellRenderer: (
        params: ICellRendererParams<CensusFileWithRowStatsDto>
      ) => {
        const status = params.value as
          | CensusZendeskTicketJobStatus
          | null
          | undefined
        if (!status) return <span style={{ color: '#bbb' }}>—</span>
        const display = ZENDESK_STATUS_DISPLAY[status] ?? {
          color: 'default',
          label: status,
        }
        return <Tag color={display.color}>{display.label}</Tag>
      },
    }
    // Insert before the File column (second-to-last, before the Review button)
    colDefs.splice(colDefs.length - 2, 0, zendeskStatusColumn)
  }
  return colDefs
}
